import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { UploadProvider } from "@/contexts/UploadQueueContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ThemeProvider } from "next-themes";
import { useAppViewportHeight } from "@/hooks/use-app-viewport-height";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Player from "./pages/Player";
import AudioPlayer from "./pages/AudioPlayer";
import Profile from "./pages/Profile";
import Vocabulary from "./pages/Vocabulary";
import Favorites from "./pages/Favorites";
import History from "./pages/History";
import Feedback from "./pages/Feedback";
import NotFound from "./pages/NotFound";
import Series from "./pages/Series";
import SeriesList from "./pages/SeriesList";
// Admin pages
import AdminLayout from "./pages/admin/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import AnalyticsDashboard from "./pages/admin/AnalyticsDashboard";
import CdkManagement from "./pages/admin/CdkManagement";
import VideoManagement from "./pages/admin/VideoManagement";
import SeriesManagement from "./pages/admin/SeriesManagement";
import AudioManagement from "./pages/admin/AudioManagement";
import UserManagement from "./pages/admin/UserManagement";
import FeedbackManagement from "./pages/admin/FeedbackManagement";
import ActivityLogs from "./pages/admin/ActivityLogs";
import UploadLogs from "./pages/admin/UploadLogs";
import AnnouncementManagement from "./pages/admin/AnnouncementManagement";
import RequestLogs from "./pages/admin/RequestLogs";

const queryClient = new QueryClient();

const App = () => {
  useAppViewportHeight();
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UploadProvider>
          <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <AnalyticsTracker />
                <Routes>
                  <Route path="/auth" element={<Auth />} />
                <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
                <Route path="/player" element={<ProtectedRoute><Player /></ProtectedRoute>} />
                <Route path="/player/:videoId" element={<ProtectedRoute><Player /></ProtectedRoute>} />
                <Route path="/series" element={<ProtectedRoute><SeriesList /></ProtectedRoute>} />
                <Route path="/series/:id" element={<ProtectedRoute><Series /></ProtectedRoute>} />
                <Route path="/audio/:audioId" element={<ProtectedRoute><AudioPlayer /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/vocabulary" element={<ProtectedRoute><Vocabulary /></ProtectedRoute>} />
                <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
                <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
                <Route path="/feedback" element={<ProtectedRoute><Feedback /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                  <Route index element={<Dashboard />} />
                  <Route path="analytics" element={<AnalyticsDashboard />} />
                  <Route path="cdk" element={<CdkManagement />} />
                  <Route path="videos" element={<VideoManagement />} />
                  <Route path="series" element={<SeriesManagement />} />
                  <Route path="audios" element={<AudioManagement />} />
                  <Route path="upload-logs" element={<UploadLogs />} />
                  <Route path="users" element={<UserManagement />} />
                  <Route path="feedback" element={<FeedbackManagement />} />
                  <Route path="logs" element={<ActivityLogs />} />
                  <Route path="announcements" element={<AnnouncementManagement />} />
                  <Route path="request-logs" element={<RequestLogs />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
            </TooltipProvider>
          </ThemeProvider>
        </UploadProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
